import { useEffect, useState, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axiosInstance';
import { saveTokens, clearTokens } from '../utils/auth';
import { Loader } from '../components/loader';

interface User {
  id: number;
  email: string;
  isApprovedEmail: boolean;
}

interface AuthContextProps {
  user: User | null;
  loading: boolean;
  login: (tokens: { accessToken: string; refreshToken: string }) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextProps>({
  user: null,
  loading: true,
  login: async () => {},
  logout: () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // avoid noisy unauthenticated requests on login/confirm pages
  const pathname = window.location.pathname;

  useEffect(() => {
    // If we're on /login or /confirmemail, skip the profile fetch to avoid 401 logs
    if (pathname === '/login' || pathname === '/confirmemail') {
      setUser(null);
      setLoading(false);
      return;
    }

    api
      .get<User>('/auth/me')
      .then(res => setUser(res.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (tokens: { accessToken: string; refreshToken: string }) => {
    saveTokens(tokens);
    const res = await api.get<User>('/auth/me');
    setUser(res.data);
    navigate('/', { replace: true });
  };

  const logout = async () => {
    clearTokens();
    setUser(null);

    await api.post<Boolean>('/auth/logout', null, { withCredentials: true });
    navigate('/login', { replace: true });
  };

  if (loading) return <Loader />;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
